/**
 * Merging several OCR passes into one answer.
 *
 * This is the scanner's main defence against confident nonsense. A recogniser
 * cannot tell you it was unsure — it returns its best guess for whatever shapes
 * it found, and a smudge that looks slightly like a 3 comes back as a 3 with a
 * respectable confidence attached.
 *
 * What it cannot do is produce the SAME wrong answer from four differently
 * processed images. Agreement across passes is therefore evidence in a way that
 * any single pass's own confidence is not:
 *
 *     Protein 8 g / 8 g / 8 g / 8 g   -> almost certainly 8 g
 *     Protein 8 g / 8 g / 3 g / 8 g   -> probably 8 g, and say so quietly
 *     Protein 8 g / 3 g / 5 g         -> do not show a number at all
 *
 * The merge is per FIELD, not per label: a pass that read protein cleanly and
 * sodium badly contributes its protein and is outvoted on sodium.
 */

import type { NutrientKey, NutrientResult, NutritionLabel } from './types';

/** Two readings of the same value are "the same" within this tolerance. */
const RELATIVE_TOLERANCE = 0.02;

function sameValue(a: number, b: number): boolean {
  if (a === b) return true;
  const scale = Math.max(Math.abs(a), Math.abs(b), 1);
  return Math.abs(a - b) / scale <= RELATIVE_TOLERANCE;
}

interface Cluster {
  value: number;
  unit: string | null;
  members: NutrientResult[];
}

/**
 * Group readings of one nutrient by the value they claim.
 *
 * Clustering rather than exact grouping so that 8 and 8.0 count as agreement,
 * while 8 and 3 do not. Unit is part of the identity: 180 mg and 180 g are not
 * the same reading, and treating them as one is how a sodium figure ends up a
 * thousand times too large.
 */
function cluster(readings: NutrientResult[]): Cluster[] {
  const clusters: Cluster[] = [];
  for (const r of readings) {
    if (r.value === null) continue;
    const existing = clusters.find(
      (c) => sameValue(c.value, r.value!) && c.unit === r.unit,
    );
    if (existing) existing.members.push(r);
    else clusters.push({ value: r.value, unit: r.unit, members: [r] });
  }
  return clusters;
}

/**
 * The consensus reading for one nutrient, or null when there is no honest one.
 *
 * `passes` is the number of OCR passes that ran, not the number that saw this
 * field — a value found by one pass out of four is much weaker evidence than
 * one found by the only pass that ran, and the score reflects that.
 */
export function mergeNutrient(
  readings: NutrientResult[],
  passes: number,
): NutrientResult | null {
  const usable = readings.filter((r) => r.value !== null);
  if (usable.length === 0) return null;

  const clusters = cluster(usable);
  if (clusters.length === 0) return null;

  clusters.sort((a, b) => {
    if (b.members.length !== a.members.length) return b.members.length - a.members.length;
    // Tie-break on the confidence the passes themselves reported.
    return best(b.members).confidence - best(a.members).confidence;
  });

  const winner = clusters[0]!;
  const runnerUp = clusters[1];
  const representative = best(winner.members);
  const agreement = winner.members.length;

  /*
   * Agreement drives the score, not the average OCR confidence.
   *
   * Averaging is the obvious move and it is wrong: three passes that all read
   * "3 g" with mediocre confidence are far better evidence than one pass that
   * read "8 g" with high confidence, and an average says the opposite.
   */
  const share = agreement / Math.max(1, passes);
  let confidence = representative.confidence * (0.45 + 0.55 * share);

  const warnings = [...representative.warnings];

  if (runnerUp !== undefined) {
    // A genuine disagreement, not just a pass that missed the field.
    const contested = runnerUp.members.length;
    const margin = agreement - contested;
    confidence *= margin >= 2 ? 0.85 : margin === 1 ? 0.6 : 0.35;
    warnings.push(
      `Passes disagreed: read as ${winner.value}${winner.unit ?? ''} ` +
        `${agreement}x and ${runnerUp.value}${runnerUp.unit ?? ''} ${contested}x.`,
    );
  } else if (agreement === 1 && passes > 1) {
    confidence *= 0.65;
    warnings.push('Only one of the reading passes found this value.');
  }

  return {
    ...representative,
    value: winner.value,
    unit: winner.unit,
    confidence: Math.max(0, Math.min(1, confidence)),
    warnings,
    agreement,
  };
}

function best(members: NutrientResult[]): NutrientResult {
  return members.reduce((a, b) => (b.confidence > a.confidence ? b : a));
}

/**
 * Merge whole labels.
 *
 * The basis is decided by majority too. It has to be: if half the passes read
 * "per 100 g" and half read a US serving panel, every downstream number would
 * be scaled by a serving size that may not apply, and a wrong basis is a much
 * larger error than a wrong nutrient.
 */
export function mergeLabels(labels: NutritionLabel[]): NutritionLabel {
  if (labels.length === 0) {
    throw new Error('mergeLabels requires at least one label');
  }
  if (labels.length === 1) return labels[0]!;

  const passes = labels.length;
  const keys = new Set<NutrientKey>();
  for (const l of labels) {
    for (const k of Object.keys(l.nutrients) as NutrientKey[]) keys.add(k);
  }

  const nutrients: Partial<Record<NutrientKey, NutrientResult>> = {};
  for (const key of keys) {
    const readings = labels
      .map((l) => l.nutrients[key])
      .filter((r): r is NutrientResult => r !== undefined);
    const merged = mergeNutrient(readings, passes);
    if (merged !== null) nutrients[key] = merged;
  }

  const basisVotes = new Map<string, number>();
  for (const l of labels) basisVotes.set(l.basis, (basisVotes.get(l.basis) ?? 0) + 1);
  const basis = [...basisVotes.entries()].sort((a, b) => b[1] - a[1])[0]![0] as
    NutritionLabel['basis'];

  const warnings = new Set<string>();
  for (const l of labels) for (const w of l.warnings) warnings.add(w);
  if (basisVotes.size > 1) {
    warnings.add(
      'The reading passes disagreed about whether the panel is per serving or per 100 g. Check the values before comparing.',
    );
  }

  // The serving size and format come from the pass that read the most fields —
  // it is the one that saw the most of the panel.
  const richest = labels.reduce((a, b) =>
    Object.keys(b.nutrients).length > Object.keys(a.nutrients).length ? b : a,
  );

  const values = Object.values(nutrients).filter((n) => n.value !== null);
  const confidence =
    values.length === 0 ? 0 : values.reduce((s, n) => s + n.confidence, 0) / values.length;

  return {
    servingSize: richest.servingSize,
    servingsPerContainer: richest.servingsPerContainer,
    basis,
    nutrients,
    confidence: basisVotes.size > 1 ? confidence * 0.8 : confidence,
    warnings: [...warnings],
    format: richest.format,
  };
}
